import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const unavailableHelperText =
  "Available inside the Import Review modal after an import package is loaded.";

const workflowStepOrder = [
  "packageExported",
  "packageImported",
  "changedFilesSaved",
  "frontendBuildCompleted",
  "backendRestartCompleted",
  "verificationSummaryCopied",
];

const defaultWorkflowLabels = {
  packageExported: "Package exported",
  packageImported: "Package imported",
  changedFilesSaved: "Changed files saved",
  frontendBuildCompleted: "Frontend build completed",
  backendRestartCompleted: "Backend restart completed",
  verificationSummaryCopied: "Verification summary copied",
};

const getStatusChipProps = (state) => {
  if (state === "success") return { color: "success", variant: "filled" };
  if (state === "failure") return { color: "error", variant: "filled" };
  if (state === "running") return { color: "warning", variant: "filled" };
  return { color: "default", variant: "outlined" };
};

const getStatusLabel = (state) => {
  if (state === "success") return "Success";
  if (state === "failure") return "Failed";
  if (state === "running") return "Running";
  return "Pending";
};

const AgentWorkflowPanel = ({
  activeChange,
  actionMessage,
  workflowStatus,
  workflowStatusLoading,
  workflowStatusError,
  onResetWorkflowStatus,
  onWorkflowStep,
  onAgentExportPackage,
  onCopyExportAgentHandoffSummary,
  onOpenImportZipWithDocs,
}) => {
  const [copyStatus, setCopyStatus] = useState("");
  const hasActiveChange = Boolean(activeChange && activeChange.id);

  const workflowRows = useMemo(
    () =>
      workflowStepOrder.map((key) => ({
        key,
        label: workflowStatus?.[key]?.label || defaultWorkflowLabels[key] || key,
        state: workflowStatus?.[key]?.state || "pending",
        timestamp: workflowStatus?.[key]?.timestamp || "",
        message: workflowStatus?.[key]?.message || "",
      })),
    [workflowStatus],
  );

  const resultSummary = useMemo(
    () =>
      [
        "DEV IDE Agent Workflow Result Summary",
        "",
        `Active change: ${
          hasActiveChange
            ? `#${activeChange.id} ${activeChange.title || ""}`.trim()
            : "None selected"
        }`,
        `Latest action message: ${actionMessage || "No action message yet."}`,
        "",
        "Workflow status:",
        ...workflowRows.map((row, index) => {
          const status = getStatusLabel(row.state);
          const timestamp = row.timestamp ? ` at ${row.timestamp}` : "";
          const message = row.message ? ` - ${row.message}` : "";
          return `${index + 1}. ${row.label}: ${status}${timestamp}${message}`;
        }),
        "",
        "Workflow checkpoints:",
        "1. Export Package - available from this panel when an active change is selected.",
        "2. Import Package - opens the existing Import ZIP with docs workflow.",
        "3. Save Changed Files - use the sticky Save All Changed Files action in the Import Review modal.",
        "4. Run Verification - use the Post-Import Verification actions in the Import Review modal after save.",
        "5. Copy Result Summary - this button copies the currently available workflow status.",
      ].join("\n"),
    [activeChange, actionMessage, hasActiveChange, workflowRows],
  );

  const handleCopyResultSummary = async () => {
    setCopyStatus("");
    try {
      await navigator.clipboard.writeText(resultSummary);
      setCopyStatus("Copied result summary");
      if (typeof onWorkflowStep === "function") {
        onWorkflowStep(
          "verificationSummaryCopied",
          "success",
          "Agent workflow result summary copied to clipboard.",
        );
      }
    } catch {
      setCopyStatus(
        "Copy failed - select and copy manually from action status if needed",
      );
      if (typeof onWorkflowStep === "function") {
        onWorkflowStep(
          "verificationSummaryCopied",
          "failure",
          "Clipboard copy failed; manual copy may be required.",
        );
      }
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        border: 1,
        borderColor: "primary.light",
        backgroundColor: "background.paper",
      }}
    >
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Agent Workflow
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use this panel as the main browser-agent path for exporting,
            importing, saving, verifying, and summarising a DEV IDE change.
          </Typography>
        </Box>

        {!hasActiveChange ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Select or create an active change before exporting an agent package.
          </Alert>
        ) : null}

        {workflowStatusError ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Workflow status is using local state because DB sync failed: {workflowStatusError}
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            size="small"
            onClick={onAgentExportPackage}
            disabled={!hasActiveChange}
          >
            1. Export Package
          </Button>

          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={onOpenImportZipWithDocs}
          >
            2. Import Package
          </Button>

          <Button variant="outlined" size="small" disabled>
            3. Save Changed Files
          </Button>

          <Button variant="outlined" size="small" disabled>
            4. Run Verification
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={handleCopyResultSummary}
          >
            5. Copy Result Summary
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => onCopyExportAgentHandoffSummary?.({ includeDiffs: false })}
            disabled={!hasActiveChange}
          >
            Copy Export Handoff
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => onCopyExportAgentHandoffSummary?.({ includeDiffs: true })}
            disabled={!hasActiveChange}
          >
            Copy Export Handoff + Diffs
          </Button>

          <Button
            variant="text"
            size="small"
            color="inherit"
            onClick={onResetWorkflowStatus}
          >
            Reset Workflow Status
          </Button>
        </Stack>

        <Paper variant="outlined" sx={{ p: 1, backgroundColor: "grey.50" }}>
          <Stack spacing={0.75}>
            <Typography variant="subtitle2">
              Agent Workflow Status{workflowStatusLoading ? " (loading saved status...)" : ""}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {workflowRows.map((row) => {
                const chipProps = getStatusChipProps(row.state);
                return (
                  <Chip
                    key={row.key}
                    size="small"
                    label={`${row.label}: ${getStatusLabel(row.state)}${
                      row.timestamp ? ` · ${row.timestamp}` : ""
                    }`}
                    color={chipProps.color}
                    variant={chipProps.variant}
                    title={row.message || row.label}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={
              hasActiveChange
                ? `Active change #${activeChange.id}`
                : "No active change"
            }
            color={hasActiveChange ? "success" : "default"}
            variant={hasActiveChange ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            label="Save + verification actions appear after import"
            variant="outlined"
          />
          {copyStatus ? (
            <Chip size="small" label={copyStatus} variant="outlined" />
          ) : null}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {unavailableHelperText} Existing manual toolbar and left-panel buttons
          remain available below.
        </Typography>

        {actionMessage ? (
          <Typography variant="caption" color="text.secondary">
            Latest action: {actionMessage}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
};

export default AgentWorkflowPanel;
